import { useMemo } from 'react';
import { toast } from '@/components/ui/sonner';
import { useTranslation } from './useTranslation';

/**
 * Centralized toast hook with i18n support
 * 
 * Usage:
 * ```typescript
 * const toast = useTranslatedToast();
 * 
 * // Translated messages (preferred)
 * toast.success('company.created');
 * toast.error('error.network');
 * 
 * // Raw messages (for dynamic content)
 * toast.raw.success(`Application from ${candidateName} received`);
 * ```
 */
export const useTranslatedToast = () => {
  const { t } = useTranslation('toast');
  
  // ✅ Use useMemo to cache the returned object - only recreate when t changes
  return useMemo(() => ({
    /**
     * Show success toast with translated message
     * @param key - Translation key from toast.json (e.g., 'company.created')
     * @param options - Optional toast configuration
     */
    success: (key: string, options?: { description?: string; duration?: number }) => {
      toast.success(t(key), options);
    },
    
    /**
     * Show error toast with translated message
     * @param key - Translation key from toast.json (e.g., 'error.network')
     * @param options - Optional toast configuration
     */
    error: (key: string, options?: { description?: string; duration?: number }) => {
      toast.error(t(key), options);
    },
    
    /**
     * Show info toast with translated message
     * @param key - Translation key from toast.json (e.g., 'info.loading')
     * @param options - Optional toast configuration
     */
    info: (key: string, options?: { description?: string; duration?: number }) => {
      toast.info(t(key), options);
    },
    
    /**
     * Show warning toast with translated message
     * @param key - Translation key from toast.json (e.g., 'warning.unsaved_changes')
     * @param options - Optional toast configuration
     */
    warning: (key: string, options?: { description?: string; duration?: number }) => {
      toast.warning(t(key), options);
    },
    
    /**
     * Show loading toast with promise handling
     * @param promise - Promise to track
     * @param messages - Loading, success, and error messages (translation keys or raw strings)
     */
    promise: <T>(
      promise: Promise<T>,
      messages: {
        loading?: string;
        success?: string | ((data: T) => string);
        error?: string | ((error: any) => string);
      }
    ) => {
      const translatedMessages = {
        loading: messages.loading ? t(messages.loading) : t('info.loading'),
        success: typeof messages.success === 'function' 
          ? messages.success 
          : (messages.success ? t(messages.success) : t('success.saved')),
        error: typeof messages.error === 'function'
          ? messages.error
          : (messages.error ? t(messages.error) : t('error.generic'))
      };
      
      return toast.promise(promise, translatedMessages);
    },
    
    /**
     * Raw toast methods for dynamic content that can't be translated
     * Use sparingly - prefer translated keys when possible
     */
    raw: {
      success: (message: string, options?: { description?: string; duration?: number }) => 
        toast.success(message, options),
      error: (message: string, options?: { description?: string; duration?: number }) => 
        toast.error(message, options),
      info: (message: string, options?: { description?: string; duration?: number }) => 
        toast.info(message, options),
      warning: (message: string, options?: { description?: string; duration?: number }) => 
        toast.warning(message, options),
    },
    
    /**
     * Dismiss a specific toast or all toasts
     */
    dismiss: (toastId?: string | number) => toast.dismiss(toastId),
  }), [t]); // ✅ Only depend on t, which is stable throughout the component lifecycle
};
